import { useEffect } from "react";
import { ChevronRight } from "lucide-react";
import TopBar from "@/components/topbar";
import { useSheetStore } from "@/zustand/sheetStore";
import BinCard from "@/components/bin-card";
import { useGetBIN } from "@/hooks/use-queries";

// ── Types ────────────────────────────────────────────────────────────────────

export type BinCardData = {
  id: number;
  name: string;
  image: string;
  price: number;
  defaultPrice?: number;
  bin: string;
  binValue: string;
  logoImage?: string;
  payBadgeBackground?: string;
  network: string;
  cardType: string;
  provider: string;
  needDepositForActiveCard: boolean;
  needCardHolder: boolean;
  minDepositAmount: number;
  maxDepositAmount: number;
  topUpFee: number;
  linesImage?: string;
  blBlurImage?: string;
  trBlurImage?: string;
  background?: string;
};

// ── Style presets ────────────────────────────────────────────────────────────

/* Card faces are designer-supplied artwork (1536x1024, 3:2), not gradients.
   Each image already bakes in its own line art, corner glow, texture and
   rounded border — so the linesImage / blBlurImage / trBlurImage / noiseImage
   overlay layers these presets used to carry are gone, and the components no
   longer render them. The logo, balance, card number, holder name and network
   mark are NOT in the artwork and remain markup overlays.

   `background` stays a CSS shorthand string so every consumer keeps working
   unchanged. The card boxes carry `aspect-3/2` to match the artwork, replacing
   the old fixed pixel heights — at any other ratio `cover` crops the artwork's
   baked outer margin and its rounded border, which reads as a misaligned edge. */
const sapphireStyle = {
  logoImage: "/images/sapphire-logo.svg",
  payBadgeBackground:
    "linear-gradient(90deg, rgba(180, 234, 255, 0.16) 0%, rgba(22, 27, 51, 0.16) 100%)",
  background: "url('/images/sapphire.webp') center / contain no-repeat",
};

const platinumVisaStyle = {
  logoImage: "/images/platinum-visa-logo.svg",
  payBadgeBackground:
    "linear-gradient(90deg, rgba(240, 229, 255, 0.16) 0%, rgba(102, 22, 167, 0.16) 100%)",
  background: "url('/images/platinum.webp') center / contain no-repeat",
};

const platinumMastercardStyle = {
  logoImage: "/images/platinum-gold-logo.svg",
  payBadgeBackground:
    " linear-gradient(155.06deg, rgba(136, 97, 48, 0.3) -8.11%, rgba(93, 52, 1, 0.3) 37.06%, rgba(23, 14, 0, 0.3) 93.93%)",
  background: "url('/images/platinumGold.webp') center / contain no-repeat",
};

// Exported for use by other sheets (create-platinum-card, pending-cards, etc.)
export const binCards = {
  sapphire: {
    id: 537100,
    name: "Sapphire Card",
    image: "",
    price: 26,
    bin: "5371 0000 0000 0000",
    binValue: "537100",
    network: "MasterCard",
    cardType: "PrepaidCard",
    provider: "int",
    needDepositForActiveCard: true,
    needCardHolder: false,
    minDepositAmount: 0,
    maxDepositAmount: 1000000,
    topUpFee: 0,
    ...sapphireStyle,
  },
  platinum: {
    id: 111068,
    name: "Platinum Visa",
    image: "",
    price: 40,
    bin: "4938 7519 0000 0000",
    binValue: "49387519",
    network: "Visa",
    cardType: "PrepaidCard",
    provider: "wsb",
    needDepositForActiveCard: true,
    needCardHolder: true,
    minDepositAmount: 1,
    maxDepositAmount: 1000000,
    topUpFee: 1,
    ...platinumVisaStyle,
  },
  platinumBasic: {
    id: 111078,
    name: "Platinum MC",
    image: "",
    price: 35,
    bin: "5240 1300 0000 0000",
    binValue: "524013",
    network: "MasterCard",
    cardType: "PrepaidCard",
    provider: "wsb",
    needDepositForActiveCard: true,
    needCardHolder: false,
    minDepositAmount: 0,
    maxDepositAmount: 1000000,
    topUpFee: 1,
    ...platinumMastercardStyle,
  },
} satisfies Record<string, BinCardData>;

// ── Helpers ──────────────────────────────────────────────────────────────────

function cardStyle(provider: string, network: string) {
  if (provider === "int") return sapphireStyle;
  if (network === "Visa") return platinumVisaStyle;
  return platinumMastercardStyle;
}

type CardStyle = {
  background: string;
  logoImage?: string;
  payBadgeBackground?: string;
  linesImage?: string;
  blBlurImage?: string;
  trBlurImage?: string;
  noiseImage?: string;
};

// Resolves style from a real card's bin prefix + network (handles "VISA"/"Visa")
export function resolveCardStyle(bin: string, network: string): CardStyle {
  if (bin?.includes("537100")) return sapphireStyle;
  if (network === "VISA" || network === "Visa") return platinumVisaStyle;
  return platinumMastercardStyle;
}

function supportText(provider: string) {
  const hi = (text: string) => (
    <strong className="text-dark-primary-main">{text}</strong>
  );
  if (provider === "int")
    return {
      supportTitle: "Supported Usage Scenarios",
      supportBody: (
        <>
          The card {hi("DOES NOT")} currently support Uber, Petrol stations,
          Talabat, Hermes Amsterdam, Hermes Paris, and Iberia and a few other
          merchants.
        </>
      ),
    };
  return {
    supportTitle: "Supported Usage Scenarios",
    supportBody: (
      <>
        The card {hi("SUPPORTS")} Uber, petrol stations, YouTube, AliExpress,
        Amazon, Iberia, Talabat.
      </>
    ),
  };
}

// ── Skeleton ─────────────────────────────────────────────────────────────────

const CardSkeleton = () => (
  <div className="bg-dark-card-3 border-dark-stroke-5 rounded-2xl border p-3 shadow-sm sm:p-4">
    <div className="bg-dark-card-4 aspect-3/2 w-full animate-pulse rounded-xl" />
    <div className="mt-3 space-y-2">
      <div className="h-2.5 w-28 bg-dark-background-light animate-pulse rounded-full" />
      <div className="h-2.5 w-full bg-dark-background-light animate-pulse rounded-full" />
      <div className="h-2.5 w-3/4 bg-dark-background-light animate-pulse rounded-full" />
    </div>
    <div className="mt-3 flex items-center justify-between border-dark-stroke-5 border-t pt-3">
      <div className="h-5 w-24 bg-dark-background-light animate-pulse rounded-full" />
      <div className="size-4 bg-dark-background-light animate-pulse rounded-full" />
    </div>
  </div>
);

// ── Shop ─────────────────────────────────────────────────────────────────────

const Shop = () => {
  const { openSheet } = useSheetStore();
  const { data: bins, isLoading } = useGetBIN();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleClick = (bin: any) => {
    if (bin.provider === "int") {
      openSheet("createSapphireCard", 2, { price: Number(bin.price) }, false);
    } else {
      openSheet(
        "createPlatinumCard",
        2,
        {
          bin: {
            id: Number(bin.id),
            bin: bin.bin,
            network: bin.network,
            price: Number(bin.price),
            defaultPrice: Number(bin.defaultPrice),
            topUpFee: Number(bin.topUpFee),
          },
        },
        false,
      );
    }
  };

  return (
    <div>
      <TopBar title="Back" backTo="/dashboard/cards" />

      <div className="px-4 pt-4 pb-8 lg:px-6 lg:pt-6">
        {/* Banner */}
        <div className="bg-dark-card-3 relative overflow-hidden rounded-2xl px-4 py-4 shadow-sm sm:px-5 sm:py-5">
          <p className="text-dark-text-50 text-sm leading-5 sm:text-base sm:leading-6">
            Buy cards for seamless online and offline use on Caviar.
          </p>
        </div>

        {/* Card grid */}
        <div className="mx-auto mt-4 grid max-w-[430px] gap-3 sm:mt-5 sm:gap-4 md:max-w-none lg:grid-cols-3">
          {isLoading ? (
            [1, 2, 3].map((i) => <CardSkeleton key={i} />)
          ) : (
            <>
              {((bins ?? []) as any[])
                .filter((b) => b.isActive)
                .map((bin, i) => {
                  const style = cardStyle(bin.provider, bin.network);
                  const { supportTitle, supportBody } = supportText(
                    bin.provider,
                  );
                  const card: BinCardData = {
                    ...style,
                    id: bin.id,
                    name: "Virtual Card",
                    image: "",
                    price: Number(bin.price),
                    defaultPrice:
                      bin.provider !== "int"
                        ? Number(bin.defaultPrice)
                        : undefined,
                    bin: bin.bin,
                    binValue: bin.bin,
                    network: bin.network,
                    cardType: bin.cardType,
                    provider: bin.provider,
                    needDepositForActiveCard: bin.needDepositForActiveCard,
                    needCardHolder: bin.needCardHolder,
                    minDepositAmount: Number(bin.minDepositAmount),
                    maxDepositAmount: Number(bin.maxDepositAmount),
                    topUpFee: Number(bin.topUpFee),
                  };

                  return (
                    <button
                      key={i}
                      onClick={() => handleClick(bin)}
                      className="block h-full w-full cursor-pointer text-left"
                    >
                      <div className="bg-dark-auxiliary-1 border-gradient-alternate relative h-full rounded-2xl p-3 shadow-sm sm:p-4">
                        
                        <BinCard card={card} className="rounded-2xl" />
                        <div className="mt-3 space-y-1.5">
                          <p className="text-dark-grey-400  text-[11px]">
                            {supportTitle}
                          </p>
                          <p className="text-dark-grey-400  min-h-10 text-[11px] leading-4 sm:min-h-12 sm:text-xs">
                            {supportBody}
                          </p>
                        </div>
                        <div className="mt-3 flex items-center justify-between border-dark-stroke-5 border-t pt-3 sm:mt-4">
                          <span className="text-dark-text-50 text-base font-semibold sm:text-lg">
                            Virtual Card
                          </span>
                          <ChevronRight className="text-dark-primary-main size-4 transition-transform duration-200 group-hover:translate-x-1" />
                        </div>
                      </div>
                    </button>
                  );
                })}

              {/* Physical card — coming soon */}
              <div className="h-full">
                <div className="group bg-dark-auxiliary-1 border-gradient-alternate relative h-full rounded-2xl p-3 shadow-sm sm:p-4">
                  <div className="bg-dark-card-4 border-dark-stroke-5 aspect-3/2 w-full overflow-hidden rounded-2xl border" />
                  <div className="mt-3 space-y-1.5">
                    <p className="text-dark-grey-400 text-[11px]">
                      Supported Usage Scenarios
                    </p>
                    <p className="text-dark-grey-400  min-h-10 text-[11px] leading-4 sm:min-h-12 sm:text-xs">
                      Everyday use for both online and offline purchase and ATM
                    </p>
                  </div>
                  <div className="mt-3 flex items-center justify-between border-dark-stroke-5 border-t pt-3 sm:mt-4">
                    <span className="text-dark-text-50 text-base font-semibold sm:text-lg">
                      Physical Card
                    </span>
                    <span className="bg-dark-card-2 text-dark-text-400 rounded-full px-3 py-1 text-[10px] tracking-[0.18em] uppercase">
                      Coming soon
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Shop;
